import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, pairwise, startWith, takeUntil } from 'rxjs/operators';
import { generateNamedMD5Hash } from 'src/util/hash';
import { TcsConfigurationService } from 'src/app/core/configuration.service';
import { UserEmailService } from '../tcs-identity/user-email.service';

export interface UserSettings {
  loggingEnabled: boolean;
  loggingCacheEnabled: boolean;
  loggingCachePubnubMessages: boolean;
  pubnubLogging: {
    level: string;
  };
}

interface UserSettingsStorageEntity extends UserSettings {
  id: string;
}

const createStorageId = (email: string) => generateNamedMD5Hash('user-settings', [email]);

const defaultEmail = 'default@email';
export const defaultUserSettings: UserSettingsStorageEntity = {
  id: createStorageId(defaultEmail),
  loggingEnabled: false,
  loggingCacheEnabled: false,
  loggingCachePubnubMessages: false,
  pubnubLogging: {
    level: 'none',
  },
};

@Injectable({
  providedIn: 'root',
})
export class TcsUserSettingsService implements OnDestroy {
  private _userSettings$: BehaviorSubject<UserSettingsStorageEntity>;
  private _isReady$ = new BehaviorSubject<boolean>(false);
  private _onDestroy$ = new Subject();

  public get defaultSettings() {
    return this._createDefaults(this.config);
  }

  public get value() {
    return this._userSettings$.value;
  }

  public get latestValue$() {
    return this._userSettings$.asObservable();
  }

  public get valueChanges$() {
    return this.latestValue$.pipe(startWith(this.defaultSettings), pairwise());
  }

  public get whenReady$() {
    return this._isReady$.pipe(
      filter((isReady) => isReady === true),
      distinctUntilChanged(),
    );
  }

  constructor(
    userEmailService: UserEmailService,
    private readonly config: TcsConfigurationService,
  ) {
    // set defaults
    this._userSettings$ = new BehaviorSubject<UserSettingsStorageEntity>(this.defaultSettings);

    userEmailService.emailChanges$.pipe(takeUntil(this._onDestroy$)).subscribe((email) => {
      this.loadUserSettings(email);
      this._isReady$.next(true);
    });
  }

  // settings should be loaded asap, so we use email since its immediately available from the okta accessToken
  public loadUserSettings(email: string | undefined) {
    const id = createStorageId(email ?? defaultEmail);
    let settings: UserSettingsStorageEntity | null = JSON.parse(localStorage.getItem(id) ?? 'null');
    if (settings == null) {
      settings = { ...this.defaultSettings, id };
      localStorage.setItem(id, JSON.stringify(settings));
    }
    const currentSettings = { ...this._userSettings$.value, ...settings };
    this._userSettings$.next(currentSettings);
  }

  public save(settings: Partial<UserSettings>) {
    const userOptions = this._userSettings$.value;
    if (userOptions != null) {
      const newOptions = { ...userOptions, ...settings };
      localStorage.setItem(userOptions.id, JSON.stringify(newOptions));
      this._userSettings$.next(newOptions);
    }
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }

  private _createDefaults(config: TcsConfigurationService): UserSettingsStorageEntity {
    const defaults: UserSettingsStorageEntity = {
      ...defaultUserSettings,
      pubnubLogging: {
        ...defaultUserSettings.pubnubLogging,
        level: config.value.pubnub.logLevel ?? defaultUserSettings.pubnubLogging.level,
      },
    };
    return defaults;
  }
}
